<template>
  <v-btn v-bind="btnProps" @click="sendMessageDialog = true">
    傳送訊息給患者

    <v-dialog v-if="sendMessageDialog" v-model="sendMessageDialog" :max-width="sendValidation.error ? 420 : 500">
      <v-card v-if="initLoading" :loading="initLoading">
        <v-card-title/>
      </v-card>
      <template v-else>
        <v-card v-if="sendValidation.isValid" class="pb-4">
          <v-btn
            fab
            icon
            absolute
            width="48"
            height="48"
            style="top: 0; right: 0; overflow: hidden;"
            @click="sendMessageDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-card-title>編輯傳送LINE訊息</v-card-title>
          <v-divider/>
          <v-card-text class="pt-4">
            <upload-img v-model="message.image" :aspect-ratio="4/3" class="mx-auto mb-4" style="max-width: 300px;"/>
            <v-textarea
              v-model="message.content"
              placeholder="請輸入LINE訊息"
              hide-details="auto"
              outlined
              clearable
              no-resize
              :maxlength="1000"
              counter
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn color="primary" width="100" @click="confirmMessage">確定內容</v-btn>
            <v-btn outlined width="100" color="grey-5" @click="sendMessageDialog = false">取消</v-btn>
          </v-card-actions>
        </v-card>
        <v-card v-else class="px-8 px-md-10 py-4">
          <v-card-title class="justify-center">操作失敗</v-card-title>
          <v-card-text class="text-center red-6--text font-weight-medium">
            {{ validationMessage }}
          </v-card-text>
          <v-card-actions>
            <div class="d-flex flex-column" style="width: 100%; gap: 20px;">
              <template v-if="sendValidation.error === 'NO_BALANCE'">
                <v-btn
                  color="red-6"
                  block
                  :href="$enum.LINE_OFFICE_WEBSITE"
                  target="_blank"
                  @click="closeMessage"
                >升級方案
                </v-btn>
                <v-btn
                  color="primary"
                  block
                  :href="$enum.DENTCO_CS_LINE"
                  target="_blank"
                  @click="closeMessage"
                >聯繫客服
                </v-btn>
                <v-btn color="grey-4" dark block @click="closeMessage">我知道了，關閉視窗</v-btn>
              </template>
              <template v-if="sendValidation.error === 'LINE_NOT_BOUND'">
                <v-btn color="grey-4" dark block @click="closeMessage">我知道了，關閉視窗</v-btn>
              </template>
            </div>
          </v-card-actions>
        </v-card>
      </template>
    </v-dialog>
    <v-dialog v-model="messageConfirmDialog" max-width="420" persistent>
      <v-card :loading="messageLoading" class="px-8 px-md-10 py-4">
        <v-card-title>將傳送以下訊息給{{ patient.patientName }}</v-card-title>
        <v-card-text>
          <v-img v-if="messageImageUrl" :src="messageImageUrl" contain/>
          <div class="text-pre-line overflow-y-auto" v-text="message.content" style="max-height: 400px;"/>
          <div class="mt-4 red-6--text font-weight-medium">
            <template v-if="hasOwnChannel">
              目前剩餘LINE訊息約：<span class="text-decoration-underline">{{ quota }}</span> 則<br>
            </template>
            本次預計使用：<span class="text-decoration-underline">{{ 1 }}</span> 則<br>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" width="100" :disabled="messageLoading" @click="sendMessage">確認傳送</v-btn>
          <v-btn outlined width="100" color="grey-5" :disabled="messageLoading" @click="messageConfirmDialog = false">
            取消
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="resultDialog" max-width="420" persistent>
      <v-card class="px-8 px-md-10 py-4">
        <v-card-title class="justify-center">{{ resultTitle }}</v-card-title>
        <v-card-text v-if="resultStatus !== 'success'" class="text-center red-6--text font-weight-medium">
          失敗原因： {{ validationMessage || messageResult }}
        </v-card-text>
        <v-card-actions>
          <div class="d-flex flex-column" style="width: 100%; gap: 20px;">
            <v-btn
              v-if="resultStatus === 'success'"
              color="grey-4"
              dark
              block
              @click="closeMessage"
            >我知道了，關閉視窗</v-btn>
            <template v-else>
              <template v-if="sendValidation.error === 'NO_BALANCE'">
                <v-btn
                  color="red-6"
                  block
                  :href="$enum.LINE_OFFICE_WEBSITE"
                  target="_blank"
                  @click="closeMessage"
                >升級方案
                </v-btn>
                <v-btn
                  color="primary"
                  block
                  :href="$enum.DENTCO_CS_LINE"
                  target="_blank"
                  @click="closeMessage"
                >聯繫客服
                </v-btn>
                <v-btn color="grey-4" dark block @click="closeMessage">我知道了，關閉視窗</v-btn>
              </template>
              <template v-else-if="sendValidation.error === 'LINE_NOT_BOUND'">
                <v-btn color="grey-4" dark block @click="closeMessage">我知道了，關閉視窗</v-btn>
              </template>
              <v-btn v-else color="grey-4" dark block @click="resultDialog = false">再試一次</v-btn>
            </template>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-btn>
</template>

<script>
import UploadImg from "../UploadImg";
import { getChannelQuotaAPI, getMessageSettingAPI, getPatientChannelBindStatusAPI, sendLineMessageAPI } from "@/api";
import { mapGetters, mapMutations } from "vuex";

export default {
  name: "SendLineMessageV2",

  props: {
    patient: {
      type: Object,
      required: true
    },
    btnProps: {
      type: Object,
      required: false,
      default: () => ({
        small: true,
        block: true,
        color: "tiffany-7",
        class: "white--text"
      })
    }
  },

  components: {
    UploadImg
  },

  data() {
    return {
      initLoading: false,
      sendMessageDialog: false,
      messageConfirmDialog: false,
      message: {
        image: null,
        content: null
      },
      messageLoading: false,
      messageImageUrl: null,

      resultStatus: 'success',
      resultDialog: false,
      resultTitle: '',
      messageResult: null,

      sendValidation: {
        isValid: false,
        error: null,
      },

      quota: 0,

      patientChannelBindStatus: null
    }
  },

  computed: {
    ...mapGetters([
      'axiosParamData',
      'ownChannelLoaded',
      'hasOwnChannel'
    ]),
    canSendMessage() {
      return this.sendValidation.isValid
    },
    validationMessage() {
      const messages = {
        NO_BALANCE: 'LINE訊息量不足。',
        LINE_NOT_BOUND: '患者沒有綁定LINE，建議改用簡訊發送。'
      }
      return messages[this.sendValidation.error] || ''
    },
    patientBind() {
      if (this.hasOwnChannel) {
        return this.patientChannelBindStatus?.own_channel_bind_status === 1
      } else {
        return this.patientChannelBindStatus?.dentco_channel_bind_status === 1
      }
    }
  },

  watch: {
    async sendMessageDialog(v) {
      if (v) {
        this.initLoading = true
        try {
          await this.checkChannelStatus()
          await this.getPatientChannelBindStatus()
          if (this.hasOwnChannel) {
            await this.getChannelQuota()
          }
          await this.checkSendEligibility()
        } catch (e) {
          console.error(e)
          alert(this.$t('GENERAL.oops'))
          this.closeMessage()
        } finally {
          this.initLoading = false
        }
      } else {
        this.reset()
      }
    },
  },

  methods: {
    ...mapMutations({
      setOwnChannel: 'setOwnChannel'
    }),
    async checkChannelStatus() {
      console.log('checkChannelStatus')
      if (!this.ownChannelLoaded) {
        const res = await getMessageSettingAPI(this.axiosParamData)
        this.setOwnChannel(res.data.own_channel)
      }
    },
    async getPatientChannelBindStatus() {
      if (this.patient.bindStatus) {
        this.patientChannelBindStatus = {
          own_channel_bind_status: 1
        }
      } else {
        const res = await getPatientChannelBindStatusAPI(this.patient.patientCode, this.axiosParamData)
        this.patientChannelBindStatus = res.data
      }
    },
    async getChannelQuota() {
      console.log('getChannelQuota')
      const res = await getChannelQuotaAPI(this.axiosParamData)
      this.quota = Number(res.data.quota) - Number(res.data.total_usage)
    },
    confirmMessage() {
      if (this.message.image || this.message.content) {
        if (this.message.image)
          this.messageImageUrl = URL.createObjectURL(this.message.image)
        this.messageConfirmDialog = true
      } else {
        alert('請至少輸入一項!')
      }
    },
    sendMessage() {
      this.messageLoading = true

      const data = {
        ...this.message,
        ...this.axiosParamData,
      }

      if (this.patient.patientCode) {
        data.patient_code = this.patient.patientCode
      } else {
        data.phone = this.patient.patientPhone
      }

      const formData = new FormData()
      for (const [key, value] of Object.entries(data)) {
        if (value)
          formData.append(key, value)
      }
      sendLineMessageAPI(formData).then(() => {
        this.resultStatus = 'success'
        this.resultTitle = '發送成功'
        this.messageConfirmDialog = false
        this.sendValidation.error = null
      }).catch(e => {
        this.resultStatus = 'error'
        this.resultTitle = '發送失敗'
        this.messageResult = e.response?.data?.messages || '發生未知錯誤，請再試一次'
        this.sendValidation.error = e.response?.data?.fail_code
      }).finally(() => {
        this.resultDialog = true
        this.messageLoading = false
      })
    },
    closeMessage() {
      this.messageConfirmDialog = false
      this.sendMessageDialog = false
      this.resultDialog = false
      this.reset()
    },
    reset() {
      this.message = this.$options.data().message
      this.messageImageUrl = this.$options.data().messageImageUrl
      this.resultStatus = this.$options.data().resultStatus
      this.resultTitle = this.$options.data().resultTitle
      this.messageResult = this.$options.data().messageResult
      this.sendValidation = this.$options.data().sendValidation
      this.quota = this.$options.data().quota
      this.patientChannelBindStatus = this.$options.data().patientChannelBindStatus
    },
    checkSendEligibility() {
      let error = null

      if (!this.patientBind) {
        error = 'LINE_NOT_BOUND'
      } else if (this.hasOwnChannel && this.quota < 1) {
        error = 'NO_BALANCE'
      }

      this.sendValidation = {
        isValid: !error,
        error
      }
    }
  }
}
</script>

<style scoped>

</style>
